import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import { hasConsent } from './hasConsent';
import {
  getJwtToken,
  getCsrfToken,
  setJwtToken as storeJwtToken,
  setCsrfToken as storeCsrfToken
} from '../../utils/authUtils';

export const AuthContext = createContext({
  userId: null,
  userName: null,
  ProfilePicture: null,
  csrfToken: null,
  jwtToken: null,
  preferredCatalog: null,
  preferredLanguage: null,
  
  login: () => {},
  logout: () => {},
  loading: true,
  isAuthenticated: false
});

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState(null);
  const [ProfilePicture, setProfilePicture] = useState(null);
  const [loading, setLoading] = useState(true);

  const [liveCsrfToken, setLiveCsrfToken] = useState('');
  const [jwtToken, setJwtTokenState] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [preferredCatalog, setPreferredCatalog] = useState(null);
  const [preferredLanguage, setPreferredLanguage] = useState(null);

  // ✅ Keep auth flag in sync
  useEffect(() => {
    
    if (localStorage.getItem("jwtToken") === "null") localStorage.removeItem("jwtToken");
if (localStorage.getItem("csrfToken") === "null") localStorage.removeItem("csrfToken");

    setIsAuthenticated(!!userId || !!getJwtToken());
  }, [userId]);

  // ✅ expose CSRF globally (your existing behavior)
  useEffect(() => {
    if (liveCsrfToken) {
      storeCsrfToken(liveCsrfToken);
      console.log("🩺 liveCsrfToken exposed globally.");
    }
  }, [liveCsrfToken]);

  const warmUpSession = async () => {
    console.log("🌐 Warming up session cookies with dummy API...");
    let retries = 0;

    while (!document.cookie.includes('session_cookie') && retries < 10) {
      try {
        await fetch(`${process.env.REACT_APP_API_BASE}/api/ping`, {
          method: 'GET',
          credentials: 'include'
        });
        console.log("✅ Session cookies are now active.");
        break;
      } catch (err) {
        console.warn(`⏳ Retry (${++retries}/10): Waiting for session cookie...`);
        await new Promise(r => setTimeout(r, 200));
      }
    }
  };

  const login = useCallback(async (
    newUserId,
    newUserName,
    csrfToken,
    newProfilePicture = null,
    token = null,
    newPreferredCatalog = null,
    newPreferredLanguage = null
  ) => {
    const sessionDuration = 60 * 60 * 1000; // 1 hour
    const expirationTime = Date.now() + sessionDuration;

    if (hasConsent()) {
      try {
        localStorage.setItem("userId", newUserId);
        localStorage.setItem("userName", newUserName);
        localStorage.setItem("expirationTime", expirationTime);
        localStorage.setItem("ProfilePicture", newProfilePicture);

        localStorage.setItem("preferredCatalog", newPreferredCatalog);
        localStorage.setItem("preferredLanguage", newPreferredLanguage || "");

        storeCsrfToken(csrfToken);

        if (token) storeJwtToken(token);
      } catch (err) {
        console.warn("⚠️ localStorage unavailable. Falling back to memory-only JWT.");
      }
    }

    setUserId(newUserId);
    setUserName(newUserName);
    setLiveCsrfToken(csrfToken);
    setProfilePicture(newProfilePicture);

    setPreferredCatalog(newPreferredCatalog);
    console.log("Normalizing catalog value:123", newPreferredCatalog);
    setPreferredLanguage(newPreferredLanguage || null);

    if (token) setJwtTokenState(token);

    warmUpSession().catch(() => {});
    setLoading(false);
    window.__AUTH_READY__ = true;
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${process.env.REACT_APP_API_BASE}/api/logout`, {
        method: 'GET',
        credentials: 'include',
      });
    } catch (err) {
      console.error("❌ Logout API call failed:", err);
    }

    try {
      localStorage.clear();
    } catch (err) {
      console.warn("⚠️ Could not clear localStorage.");
    }

    setUserId(null);
    setUserName(null);
    setLiveCsrfToken('');
    setProfilePicture(null);
    setJwtTokenState(null);

    setPreferredCatalog('Hockey');
    setPreferredLanguage(null);

    storeJwtToken(null);
    storeCsrfToken(null);

    setLoading(false);
    window.__AUTH_READY__ = true;
    navigate('/home');
  }, [navigate]);

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedUserName = localStorage.getItem('userName');
    const expirationTime = localStorage.getItem('expirationTime');
    const storedProfilePicture = localStorage.getItem('ProfilePicture');

    const storedCsrfToken = getCsrfToken();
    const storedJwtToken = getJwtToken();

    const storedPreferredCatalog = localStorage.getItem('preferredCatalog');
    const storedPreferredLanguageRaw = localStorage.getItem('preferredLanguage');
    const storedPreferredLanguage =
      storedPreferredLanguageRaw && storedPreferredLanguageRaw !== "null" && storedPreferredLanguageRaw !== "undefined"
        ? storedPreferredLanguageRaw
        : null;

    // ✅ set prefs early to avoid UI flicker
    setPreferredCatalog(storedPreferredCatalog || null);
    setPreferredLanguage(storedPreferredLanguage);

    if (
      storedUserId && storedUserName && storedCsrfToken &&
      expirationTime && Date.now() < parseInt(expirationTime, 10)
    ) {
      setUserId(storedUserId);
      setUserName(storedUserName);
      setProfilePicture(storedProfilePicture);
      setLiveCsrfToken(storedCsrfToken);
      setJwtTokenState(storedJwtToken);

      warmUpSession();
      setLoading(false);
      window.__AUTH_READY__ = true;
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/api/checkAuth`, {
          credentials: 'include',
          headers: storedJwtToken ? { Authorization: `Bearer ${storedJwtToken}` } : {}
        });

        if (!response.ok) throw new Error(`Auth check failed: ${response.status}`);
        const data = await response.json();

        if (data.success) {
          await login(
            data.userId,
            data.userName,
            data.csrfToken,
            data.ProfilePicture,
            data.token,
            data.preferredCatalog || data.PreferredCatalog || 'null',
            data.preferredLanguage || data.PreferredLanguage || null
          );
        }
      } catch (error) {
        console.error('❌ Error during checkAuth():', error.message || error);
      } finally {
        setLoading(false);
        window.__AUTH_READY__ = true;
      }
    };

    checkAuth();

    const intervalId = setInterval(() => {
      const expiration = localStorage.getItem('expirationTime');
      if (expiration && Date.now() > parseInt(expiration, 10)) logout();
    }, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [login, logout]);

  return (
    <AuthContext.Provider
      value={{
        userId,
        userName,
        ProfilePicture,
        liveCsrfToken,
        jwtToken: getJwtToken(),
        preferredCatalog,
        preferredLanguage,
        setPreferredCatalog,
      setPreferredLanguage,
        login,
        logout,
        loading,
        isAuthenticated
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
